import React, { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { haalMeldingsmonitor } from '../../services/professionalDataApi';
import { Kaart, Laden } from '../data/DataFilterBalk';
import { DonutChart, Legenda, REEKS_KLEUREN, euro } from '../data/DataCharts';
import { KolomGrafiek } from './Grafieken';
import { FilterRij, Fout, Keuze, jaarOpties } from './Bediening';

const OVERIG_KLEUR = '#9ca3af';
const pctNl = (v: number) => `${v.toLocaleString('nl-NL', { maximumFractionDigits: 1 })}%`;

export const MeldingsmonitorOnderdeel: React.FC = () => {
  const { t } = useTranslation('professionalData');
  const nu = new Date();
  const [jaar, setJaar] = useState(nu.getFullYear());
  const [kwartaal, setKwartaal] = useState(Math.floor(nu.getMonth() / 3) + 1);
  const q = useQuery({ queryKey: ['professional-data', 'meldingsmonitor', jaar, kwartaal], queryFn: () => haalMeldingsmonitor(jaar, kwartaal), placeholderData: keepPreviousData });
  const d = q.data;

  // Maximaal 7 opdrachtgevers in vaste kleurvolgorde, de rest als "overig"
  const zichtbaar = d?.concentratie.slice(0, 7) ?? [];
  const rest = d?.concentratie.slice(7) ?? [];
  const segmenten = [
    ...zichtbaar.map((c, i) => ({ label: c.naam, waarde: c.pct, netto: c.netto, kleur: REEKS_KLEUREN[i] })),
    ...(rest.length
      ? [{ label: `${t('meldingsmonitor.opdrachtgevers')} (+${rest.length})`, waarde: Math.round(rest.reduce((s, c) => s + c.pct, 0) * 10) / 10, netto: rest.reduce((s, c) => s + c.netto, 0), kleur: OVERIG_KLEUR }]
      : []),
  ];

  const reeksen = [
    { key: 'actief', label: t('meldingsmonitor.actief'), kleur: REEKS_KLEUREN[0] },
    { key: 'afgerond', label: t('meldingsmonitor.afgerond'), kleur: REEKS_KLEUREN[2] },
  ];

  return (
    <div className="space-y-6">
      <FilterRij>
        <Keuze label={t('meldingsmonitor.jaar')} waarde={jaar} onChange={setJaar} opties={jaarOpties()} />
        <Keuze label={t('meldingsmonitor.kwartaal')} waarde={kwartaal} onChange={setKwartaal} opties={[1, 2, 3, 4].map((k) => ({ waarde: k, label: `Q${k}` }))} />
      </FilterRij>

      {q.isLoading && <Laden />}
      {q.isError && <Fout fout={q.error} />}
      {d && (
        <>
          <Kaart titel={t('meldingsmonitor.concentratieTitel')} uitleg={t('meldingsmonitor.concentratieUitleg', { jaar: d.jaar })}>
            {segmenten.length === 0 ? (
              <p className="text-sm text-gray-500">{t('geenData')}</p>
            ) : (
              <>
                <div className="flex flex-col items-center gap-6 md:flex-row md:items-start">
                  <DonutChart segmenten={segmenten.map((s) => ({ label: s.label, waarde: s.waarde, kleur: s.kleur }))} centrumWaarde={String(d.opdrachtgevers)} centrumLabel={t('meldingsmonitor.opdrachtgevers')} />
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                        <th className="py-2 pr-3 font-medium">{t('meldingsmonitor.opdrachtgever')}</th>
                        <th className="py-2 pr-3 text-right font-medium">{t('meldingsmonitor.aandeel')}</th>
                        <th className="py-2 text-right font-medium">{t('meldingsmonitor.netto')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {segmenten.map((s) => (
                        <tr key={s.label} className="border-b border-gray-100">
                          <td className="py-2 pr-3">
                            <span className="mr-2 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: s.kleur }} />
                            {s.label}
                          </td>
                          <td className="py-2 pr-3 text-right font-medium">{pctNl(s.waarde)}</td>
                          <td className="py-2 text-right">{euro(s.netto, 2)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td className="py-2 pr-3 font-semibold" />
                        <td className="py-2 pr-3 text-right font-semibold">100%</td>
                        <td className="py-2 text-right font-semibold">{euro(d.omzet_totaal, 2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-4 flex items-start gap-2 text-xs text-gray-600">
                  <Info className="mt-px h-3.5 w-3.5 flex-shrink-0 text-navy-700" aria-hidden />
                  {d.opdrachtgevers === 1 ? t('meldingsmonitor.eenOpdrachtgever') : t('meldingsmonitor.grootste', { pct: pctNl(d.grootste_pct ?? 0) })}
                </p>
              </>
            )}
          </Kaart>

          <Kaart titel={t('meldingsmonitor.poolTitel', { kwartaal: d.kwartaal, jaar: d.jaar })} uitleg={t('meldingsmonitor.poolUitleg')}>
            {d.pools.length === 0 ? (
              <p className="text-sm text-gray-500">{t('geenData')}</p>
            ) : (
              <>
                <div className="mb-3">
                  <Legenda items={reeksen.map((r) => ({ label: r.label, kleur: r.kleur }))} />
                </div>
                <KolomGrafiek
                  categorieen={d.pools.map((p) => ({
                    key: p.employer_id,
                    label: p.naam,
                    waarden: { actief: p.actief, afgerond: p.afgerond },
                    sub: p.gestart > 0 ? t('meldingsmonitor.gestart', { count: p.gestart }) : undefined,
                  }))}
                  reeksen={reeksen}
                  formaat={(v) => String(Math.round(v))}
                />
              </>
            )}
          </Kaart>
        </>
      )}
    </div>
  );
};
